import React from 'react';
import PropTypes from 'prop-types';
import {withRouter, routerShape, locationShape} from 'react-router';
import {action, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import * as Sentry from '@sentry/react';

import isValidException from 'utils/isValidException';
import Button from '@urbanmassage/component-library/lib/Button/Button';
import Input from '@urbanmassage/component-library/lib/Input/Input';
import Link from '@urbanmassage/component-library/lib/Link/Link';
import Typography, { TypeVariants } from '@urbanmassage/component-library/lib/Typography/Typography';

import {Form, FormHandler} from 'spa/Components/Form';
import {withI18n} from 'spa/Components/Context';

import autobind from 'autobind-decorator';

import './address-not-serviced.less';

@withRouter
@inject(({
  store: {
    notifications,
    searchAttempt,
    session,
    tracker,
  },
}) => (
  {
    notifications,
    searchAttempt,
    session,
    tracker,
  }
))
@observer
@withI18n
class AddressNotServiced extends React.Component {
  static propTypes = {
    location: locationShape.isRequired,
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
    }).isRequired,
    onClickNewSearch: PropTypes.func.isRequired,
    router: routerShape.isRequired,
    search: PropTypes.shape({
      address_id: PropTypes.string,
      place_id: PropTypes.string,
      provider_id: PropTypes.string,
    }).isRequired,
    searchAttempt: PropTypes.shape({
      addSearchAttempt: PropTypes.shape({
        put: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
    session: PropTypes.shape({
      isLoggedIn: PropTypes.bool,
      user: PropTypes.shape({
        email: PropTypes.string,
      }),
    }).isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
  };

  @observable emailAddress;
  @observable emailInputErrors = [];
  @observable isLoading = false;
  @observable isSuccess = false;

  @autobind
  @action('onChangeEmailAddress - AddressNotServiced')
  onChangeEmailAddress(event) {
    this.emailAddress = event.currentTarget.value;
  }

  @autobind
  @action('clickNewSearch - AddressNotServiced')
  clickNewSearch(event) {
    const {onClickNewSearch, tracker} = this.props;

    tracker.track('Clicked try new search on address not serviced', {
      category: 'Address not serviced',
    });

    if (onClickNewSearch) {
      onClickNewSearch();
    }
  }

  @autobind
  @action('onSubmit - AddressNotServiced')
  onSubmit(event) {
    event.preventDefault();

    const {i18n} = this.context;
    const {
      notifications,
      search,
      searchAttempt: {
        addSearchAttempt,
      },
      session: {
        isLoggedIn,
      },
      tracker,
    } = this.props;

    this.emailInputErrors = [];

    if (!isLoggedIn && (!this.emailAddress || this.emailAddress === '')) {
      this.emailInputErrors.push(i18n._x('Email address is required', 'Address not serviced'));

      return;
    }

    this.isLoading = true;

    tracker.track('User submitted address search attempt', {
      category: 'Address not serviced',
      address_id: search.address_id,
      place_id: search.place_id,
    });

    if (!this.isSuccess) {
      addSearchAttempt.put('', {
        email: !isLoggedIn ? this.emailAddress : null,
        ...search,
      })
        .then((response) => {
          this.isSuccess = true;
          this.isLoading = false;
        }, (error) => {
          this.isLoading = false;

          tracker.track('Error in address search attempt post', {
            category: 'Address not serviced',
            responseError: error && error.json,
          });

          if (isValidException(error)) {
            Sentry.captureException(error);
          } else {
            Sentry.captureMessage('Address not serviced failure to submit search attempt');
          }

          this.emailInputErrors.push(i18n.__error(error));

          notifications.add({
            id: 'addSearchAttemptError',
            type: 'error',
            title: i18n.__error(error),
          });
        });
    }
  }

  render() {
    const {i18n} = this.context;
    const {
      location,
      session: {
        isLoggedIn,
        user,
      },
    } = this.props;
    const {clickNewSearch} = this;

    return (
      <div className="address-not-serviced v-container">
        <div className="address-not-serviced__top-text-container">
          <Typography variant={TypeVariants.h2} theme="darkTitle" margin="0 auto 2.4rem">
            {i18n._x('Sorry, this address is not yet in our service area', 'Address not serviced')}
          </Typography>
          <Typography variant={TypeVariants.bodyXLarge} theme="secondary" margin="0 auto 2rem">
            {i18n._x('You’ll be the first to know', 'Address not serviced')}
          </Typography>
          <Typography variant={TypeVariants.bodyXLarge} theme="secondary" margin="0 auto 2rem">
            {isLoggedIn ? (
              i18n._x('Click the button below, and we will let you know when we service to your area.', 'Address not serviced')
            ) : (
              i18n._x('Leave your email address and we will let you know when we service to your area.', 'Address not serviced')
            )}
          </Typography>
        </div>
        <FormHandler method="POST">
          <Form
            to={{
              pathname: location.pathname,
              query: location.query,
            }}
            method="POST"
            disabled={this.isLoading}
          >
            <div className="address-not-serviced__container">
              <div className="address-not-serviced__input-container">
                <Input
                  disabled={this.isLoading}
                  errors={this.emailInputErrors}
                  hidden={isLoggedIn}
                  id="email"
                  invalid={this.emailInputErrors.length}
                  label={i18n._x('Email Address', 'Address not serviced')}
                  name="email"
                  onChange={this.onChangeEmailAddress}
                  placeholder={i18n._x('john@smith.com', 'Address not serviced')}
                  required={!isLoggedIn}
                  type="email"
                  value={user && user.email}
                />
              </div>
              {this.isSuccess ? (
                <Button
                  block
                  data-testid="address-not-serviced-success"
                  tag="submit"
                  theme="success"
                >
                  {i18n._x('Thank you!', 'Address not serviced')}
                </Button>
              ) : (
                <Button
                  block
                  data-testid="address-not-serviced-submit"
                  isLoading={this.isLoading}
                  tag="submit"
                  onClick={this.onSubmit}
                >
                  {i18n._x('Notify me!', 'Address not serviced')}
                </Button>
              )}
              <div className="address-not-serviced__new-search-container">
                <Link
                  className="address-not-serviced__new-search-button"
                  data-testid="address-not-serviced-new-search"
                  onClick={clickNewSearch}
                  text={i18n._x('try searching for a different address', 'Address not serviced')}
                />
              </div>
            </div>
          </Form>
        </FormHandler>
      </div>
    );
  }
}

export default AddressNotServiced;



// WEBPACK FOOTER //
// ./spa/Components/AddressSelector/AddressNotServiced.jsx